import type ru from '@/i18n/locales/ru'

/**
 * Английский. Тип берём из русской локали: пропущенный ключ станет ошибкой
 * сборки, а не молчаливым русским текстом посреди английского экрана.
 */
const en: typeof ru = {
  common: {
    next: 'Next',
    save: 'Save',
    confirm: 'Confirm',
    yes: 'Yes',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    clear: 'Clear',
    send: 'Send',
    apply: 'Apply',
    choose: 'Choose',
    done: 'Done',
    expand: 'Expand',
    less: 'Less',
    more: 'More',
    map: 'Map',
    total: 'Total',
    calculation: 'Summary',
    sum: 'sum',
  },

  nav: {
    home: 'Home',
    menu: 'Menu',
    cart: 'Cart',
    orders: 'Orders',
    profile: 'Profile',
  },

  units: {
    ml: 'ml',
    g: 'g',
    kcal: 'kcal',
    protein: 'protein',
    fat: 'fat',
    carbs: 'carbs',
    per100: 'Per 100 g',
    min: 'min',
  },

  delivery: {
    delivery: 'Delivery',
    pickup: 'Pickup',
    takeaway: 'Pick up',
    free: 'Free',
  },

  toast: {
    success: 'Done',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  },

  errors: {
    unknown: 'Unknown error',
    timeout: 'Request timed out',
    offline: 'No connection to the server',
    request: 'Request error',
    captchaUnavailable: 'Captcha is unavailable',
    captchaFailed: 'reCAPTCHA failed to load',
    geoDenied: 'Location access denied — enable it in settings or pick the point manually',
    geoFailed: 'Could not detect your location — pick the point manually',
    geoDeniedMap: 'Location access denied — enable it in settings or pick the address on the map',
    geoSkipped: 'No location is fine — pick the address on the map',
    pointNotFound: 'Could not find the point by address — pick it on the map',
    pointRequired: 'Pick a point on the map',
  },

  onboarding: {
    languageTitle: 'Choose a language',
    orderTypeTitle: 'Order type',
    introTitle: 'Order in a couple of minutes —\nright inside Telegram',
    geoTitle: 'Where are you?',
    geoText:
      'We will detect your city and show the menu, prices and the nearest restaurant. It matters:\nthey differ from city to city.',
    geoAction: 'Allow location',
    searchingTitle: 'One moment...',
    searchingText: 'Looking for you on the map\nand for the nearest restaurant',
    addressPlaceholder: 'Enter an address',
  },

  auth: {
    signUp: 'Sign up',
    signIn: 'Sign in',
    phoneTitle: 'Your number',
    phoneLabel: 'Phone number',
    telegramShare: 'Share via Telegram',
    codeTitle: 'Enter the code',
    codeHint: 'We sent an SMS with the confirmation code',
    codeResend: 'Send the code again',
    codeSent: 'The message has been sent to your phone number',
    signedIn: 'You are signed in',
  },

  home: {
    bannerProductMissing: 'This item is not on the menu of your branch right now',
    addressLabel: 'Delivery address',
    addressEmpty: 'Set an address',
    branchEmpty: 'Choose a branch',
    searchPlaceholder: 'lavash, doner, pepsi...',
    hits: 'Hits of the week',
    novelties: 'New items',
  },

  menu: {
    notFound: 'Nothing found',
    unavailable: 'Menu is unavailable',
    tryAnotherQuery: 'Try changing the query',
    tryAnotherAddress: 'Try another address or refresh the page',
  },

  product: {
    add: 'Add',
    composition: 'Ingredients',
    extraModifiers: 'Additional modifiers',
    without: 'No {name}',
    upsell: 'Often bought with this',
    addMore: 'Add {count} more',
    removeSome: 'Remove {count}',
    addToCart: 'Add to cart',
    removeFromCart: 'Remove from cart',
    addToFavorites: 'Add to favorites',
    removeFromFavorites: 'Remove from favorites',
  },

  cart: {
    offerNow: 'right now',
    offerAdd: 'Add for {sum}',
    offerSkip: 'Thanks, not now',
    title: 'Cart',
    cutlery: 'Cutlery',
    items: 'item | items | items',
    upsell: 'Often bought with this',
    empty: 'Your cart is empty',
    emptyText: 'Add something from the menu',
    checkout: 'Place an order',
    discount: 'Discount',
  },

  checkout: {
    title: 'Checkout',
    submit: 'Place order',
    branch: 'Branch',
    branchEmpty: 'No branch selected',
    payment: 'Payment',
    recipient: 'Recipient',
    recipientSelf: 'For me',
    recipientOther: 'For someone else',
    recipientTo: 'Deliver to',
    nameRequired: 'Enter a name',
    addressLabel: 'Delivery address',
    addressEmpty: 'Set an address',
    entrance: 'Entrance',
    floor: 'Floor',
    flat: 'Flat/Office',
    addressComment: 'Address details',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone number',
    products: 'Items',
    promo: 'Promo code',
    promoDiscount: 'Promo code discount',
    deliveryDiscount: 'Delivery discount',
    promoTitle: 'Your promo codes',
    promoPlaceholder: 'Paste a promo code',
    promoInvalid: 'This promo code does not apply to this order',
    pointsSpend: 'Spend points',
    pointsRate: '1 point = 1 sum',
    pointsWithdraw: 'Paid with points',
    successTitle: 'Thank you for your order!',
    successNumber: 'Order number:',
    successText: 'No need to wait for a call — we will let you know when there are updates.',
    successPay: 'Pay for the order',
    successTrack: 'Track the order',
    successTrackNumber: 'Order {number} • track',
    successHome: 'To home',
  },

  orders: {
    free: 'Free',
    discount: 'Discount',
    title: 'Order history',
    tabActive: 'Active',
    tabDone: 'Completed',
    emptyActive: 'No active orders',
    emptyDone: 'Nothing here yet',
    emptyText: 'Placed orders will show up in this list',
    order: 'Order',
    repeat: 'Repeat',
    repeatMissing: 'These items are not on the menu right now: {items}',
    track: 'Track',
    yourOrder: 'Your order',
    notFound: 'Order not found',
    support: 'Contact support',
    noName: 'No name',
    address: 'Address',
    canceled: 'Order canceled',
    cancel: 'Cancel the order',
    refund: 'We will refund after delivery',
    courierChat: 'Message the courier',
    courierCall: 'Call the courier',
    route: 'Build a route',
    rateTitle: 'How was your order?',
    rateText: 'Rate the order. It helps us get better.',
    rateStar: 'Rating {star}',
  },

  status: {
    accepted: 'Accepted',
    acceptedText: 'The restaurant confirmed the order.',
    acceptedPickupText: 'We got your order and passed it\nto the kitchen.',
    cooking: 'Cooking',
    cookingText: 'The chefs are already cooking — almost there.',
    cookingPickupText: 'We are cooking your order. Almost there.',
    onWay: 'Courier on the way',
    onWayText: 'The courier picked up the order and is on the way.',
    near: 'Courier is near',
    nearText: "I'm close, five more minutes!",
    delivered: 'Order delivered',
    deliveredText: 'Enjoy your meal! Points have been credited.',
    ready: 'Ready for pickup',
    readyText: 'The order is ready. We are waiting for you.',
  },

  profile: {
    title: 'Profile',
    myData: 'My details',
    language: 'Language',
    support: 'Support',
    feedback: 'Feedback',
    supportContacts: 'Chat bot or 78 150 00 30',
    addName: 'Add a name',
    noPhone: 'No phone number',
    edit: 'Edit profile',
    addPhoto: 'Add a photo',
    logout: 'Log out',
    logoutConfirm: 'Log out of your account?',
    logoutAction: 'Log out',
    photoTitle: 'Add a photo',
    photoCamera: 'Camera',
    photoGallery: 'Gallery',
    photoFailed: 'Could not read the photo',
  },

  settings: {
    title: 'Settings',
    personal: 'Personal details',
    avatarTitle: 'Choose an avatar',
    phone: 'Phone number',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    birthday: 'Date of birth',
    birthdayEmpty: 'Choose a date',
    genderTitle: 'Choose gender',
    male: 'Male',
    female: 'Female',
    saved: 'Details saved',
  },

  points: {
    unit: 'points',
    heroTitle: 'Points\nare waiting',
    balance: 'Your balance',
    howTitle: 'How points work',
    ruleEarn: 'We return 5% of the item price in points right after you get your order. Promotions can give even more cashback.',
    ruleSpend:
      '1 point = 1 sum. Pay for items with points almost entirely — at least 1 000 sum is paid with money. Points do not cover delivery.',
    ruleExpiry: 'Points last 3 months. If they are about to expire, we will remind you in Telegram 7 days ahead.',
    ruleProlong: 'Every new order extends all your points for another 3 months. Keep ordering and nothing expires.',
    historyTitle: 'History',
    historyEmpty: 'Earned and spent points will appear here',
  },

  promo: {
    title: 'My promo codes',
    available: 'Available promo codes',
    empty: 'No promo codes yet',
    emptyText: 'Follow our offers — your promo codes will appear here',
    copy: 'Copy the code',
    copied: 'Promo code copied',
    copyFailed: 'Could not copy the code',
    until: 'until {date}',
    freeDelivery: 'Free delivery',
    gift: 'Gift with the order',
    orderDiscount: 'Order discount',
    percentOff: '{percent}% off the order',
    amountOff: '{amount} off',
    minOrder: 'from {amount}',
    itemsDiscount: 'Discount on selected items',
  },

  promotion: {
    deliveryLeft: 'to free delivery',
    deliveryDone: 'free delivery is yours',
    bonusLeft: 'to a free bonus',
    bonusDone: 'the bonus is yours',
    allDone: 'All bonuses unlocked!',
  },

  favorites: {
    title: 'Favorites',
    subtitle: 'Your favorite items',
    empty: 'Nothing here yet',
    emptyText: 'Add items to favorites to find them quickly and order again',
    clearConfirm: 'Clear favorites?',
  },

  cards: {
    title: 'Cards',
    mine: 'My cards',
    empty: 'No cards yet',
    emptyText: 'Link a card to pay for orders faster',
    add: 'Add a card',
    cash: 'Cash',
  },

  notifications: {
    title: 'Notifications',
    tabNews: 'News',
    tabPromotions: 'Offers',
    empty: 'No notifications',
    emptyText: 'News and offers from Oqtepa Lavash will appear here',
  },

  chat: {
    title: 'Online chat',
    placeholder: 'Message',
    closed: 'Chat closed',
    closedHint: 'This request is closed — you can no longer write here',
    call: 'Call support',
    callConfirm: 'Call the call centre?',
    attach: 'Attach a file',
    empty: 'No requests yet',
    request: 'Support request',
    uploading: 'Uploading the file…',
    uploadFailed: 'Could not upload the file',
    photo: 'Photo',
    noticeTitle: 'New message from support',
    noticeOpen: 'Open the chat',
    unreadHint: 'Support replied — open the chat',
  },

  address: {
    title: 'My addresses',
    listTitle: 'Delivery addresses',
    label: 'Delivery address',
    fallbackLabel: 'Address',
    new: 'New address',
    edit: 'Edit the address',
    emptyAccent: 'Add',
    emptyTitle: 'your first address',
    emptyText: 'Set a convenient address to place orders in just a few taps.',
    add: 'Add an address',
    save: 'Save the address',
    remove: 'Delete the address',
    removeConfirm: 'Delete the address?',
    name: 'Address name',
    entrance: 'Entrance',
    floor: 'Floor',
    flat: 'Flat/Office',
    entranceShort: 'entrance',
    floorShort: 'floor',
    flatShort: 'flat',
    comment: 'Address details',
    detecting: 'Detecting the address…',
    whereTo: 'Where to deliver?',
    findMe: 'Find me',
  },

  branch: {
    title: 'Choose a branch',
    where: 'Where will you pick it up?',
    search: 'Search for a branch',
    nearest: 'Show the nearest',
    notFound: 'No branches found',
    workTime: 'Hours:',
    workTimeShort: 'Hours:',
    address: 'Address:',
    phone: 'Phone:',
  },
}

export default en
